import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import MatrixBackground from "../components/MatrixBackground";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Zap, ShoppingBag, Store, Bike, Shield, ArrowRight, Timer, Package, MapPinned } from "lucide-react";

const SURFACES = [
  { icon: ShoppingBag, title: "Customer App", desc: "Shop groceries, food, meds in 10 mins.", path: "/app", role: "customer", testid: "surface-customer" },
  { icon: Store, title: "Merchant Panel", desc: "Live inventory, orders & payouts.", path: "/merchant", role: "merchant", testid: "surface-merchant" },
  { icon: Bike, title: "Rider Dashboard", desc: "Tap-to-update deliveries on the move.", path: "/rider", role: "rider", testid: "surface-rider" },
  { icon: Shield, title: "Admin Control", desc: "Shops, riders, analytics in one view.", path: "/admin", role: "admin", testid: "surface-admin" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const roleHome = user ? (
    user.role === "customer" ? "/app" :
    user.role === "merchant" ? "/merchant" :
    user.role === "rider" ? "/rider" :
    "/admin"
  ) : null;

  return (
    <div className="min-h-screen grid-bg noise relative isolate overflow-hidden" data-testid="landing-container">
      <MatrixBackground opacity={0.3} />
      {/* Decorative orbs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-blue-600/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-cyan-400/15 blur-3xl" />

      {/* nav */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Button variant="ghost" className="text-slate-200 hover:bg-white/5" onClick={() => navigate("/login")} data-testid="landing-login">
                Sign in
              </Button>
              <Button className="btn-neon rounded-full px-5" onClick={() => navigate("/register")} data-testid="landing-register">
                Get started
              </Button>
            </>
          ) : (
            <Button className="btn-neon rounded-full px-5" onClick={() => navigate(roleHome)} data-testid="landing-dashboard">
              Open your dashboard <ArrowRight size={16} className="ml-1.5" />
            </Button>
          )}
        </div>
      </div>

      {/* hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-[11px] uppercase tracking-widest font-bold text-cyan-300" data-testid="hero-badge">
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className="pulse-dot absolute inset-0 rounded-full" />
              <span className="relative rounded-full w-1.5 h-1.5 bg-cyan-400" />
            </span>
            Streamline Stack Solutions
          </div>

          <h1 className="heading-font mt-5 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]">
            Everyday essentials.<br />
            <span className="text-gradient">In ten minutes.</span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            STACK is a quick-commerce rail connecting you to local shops for groceries,
            food, medicine and essentials — fulfilled by nearby riders at electric speed.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              className="btn-neon rounded-full px-6 h-12 text-base"
              onClick={() => navigate(user ? roleHome : "/register")}
              data-testid="hero-cta-primary"
            >
              <Zap size={18} className="mr-2" />
              {user ? "Open your dashboard" : "Start ordering"}
            </Button>
            <Button
              variant="outline"
              className="rounded-full px-6 h-12 text-base border-cyan-500/30 bg-white/5 hover:bg-white/10 text-white"
              onClick={() => navigate("/app")}
              data-testid="hero-cta-browse"
            >
              Browse as guest <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            <Stat icon={Timer} label="Avg. ETA" value="9m 42s" />
            <Stat icon={Package} label="SKUs live" value="12k+" />
            <Stat icon={MapPinned} label="Dark stores" value="86" />
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-6 bg-cyan-500/10 blur-3xl rounded-[3rem]" />
            <div className="relative glass rounded-[2rem] p-5 ring-cyan">
              <img
                alt="Fresh groceries"
                src="https://images.pexels.com/photos/11831458/pexels-photo-11831458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                className="w-full h-72 object-cover rounded-2xl"
              />
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Live order</div>
                  <div className="heading-font text-xl font-bold">Rider on the way</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-slate-400">arriving in</div>
                  <div className="heading-font text-2xl font-extrabold text-cyan-300">4:12</div>
                </div>
              </div>
              <div className="mt-4 h-1 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full w-2/3 shimmer" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role surfaces */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="heading-font text-2xl sm:text-3xl font-bold">Four surfaces. One stack.</h2>
            <p className="text-slate-400 text-sm sm:text-base mt-1">Jump into any role to experience the full operations loop.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SURFACES.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.path}
                to={s.path}
                data-testid={s.testid}
                className="group glass rounded-2xl p-5 hover:ring-cyan transition relative overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-cyan-400/10 blur-2xl rounded-full group-hover:bg-cyan-400/20 transition" />
                <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-400/20 border border-cyan-500/30 flex items-center justify-center">
                  <Icon size={22} className="text-cyan-300" />
                </div>
                <div className="relative mt-4">
                  <div className="heading-font text-lg font-bold">{s.title}</div>
                  <div className="text-slate-400 text-sm mt-1">{s.desc}</div>
                </div>
                <div className="relative mt-4 flex items-center gap-1 text-cyan-300 text-xs font-bold uppercase tracking-widest">
                  Enter <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <footer className="relative z-10 border-t border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-xs text-slate-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Streamline Stack Solutions</div>
          <div className="hidden sm:block">Built for electric speed.</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-cyan-300">
        <Icon size={14} />
        <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
      </div>
      <div className="heading-font text-lg font-extrabold mt-1">{value}</div>
    </div>
  );
}
